package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The claimant's relationship to the business they're claiming — surfaced so the admin reviewer can
 * weigh the request. Serialized as a stable lowercase wire key.
 */
@Serializable
enum class ClaimRole {
    @SerialName("owner") Owner,
    @SerialName("manager") Manager,
    @SerialName("employee") Employee,
    @SerialName("other") Other,
}

/** Review lifecycle of a submitted claim (`claims/{claimId}.status`). Admin resolves pending ones. */
@Serializable
enum class ClaimReviewStatus {
    @SerialName("pending") Pending,
    @SerialName("approved") Approved,
    @SerialName("rejected") Rejected,
}

/**
 * A verification request to take ownership of a directory listing, stored as `claims/{claimId}` (see
 * `docs/DATA_MODEL.md`). Kept in its own collection rather than on the business doc so the claimant can
 * attach verification details (role, contact, a free-text "how to verify" note, an optional proof
 * reference) without polluting the public business record. On approval the admin maps
 * [claimantUid] onto `businesses/{businessId}.ownerUid`.
 *
 * [businessName] is denormalized so the admin queue can render without a second read. Timestamps are
 * epoch millis (or null while a server timestamp is still resolving), keeping this model free of any
 * Firestore type.
 */
@Serializable
data class BusinessClaim(
    val id: String,
    val businessId: String,
    val businessName: String,
    val claimantUid: String,
    val claimantName: String,
    val role: ClaimRole = ClaimRole.Owner,
    val contactEmail: String,
    val contactPhone: String,
    /** Free text: how the claimant can prove the relationship (documents on file, on-site, etc.). */
    val message: String,
    /**
     * Download URL of the claimant's uploaded proof photo (business licence, bill, storefront…),
     * stored privately under `claim-proofs/{claimantUid}/` (claimant + admin read only). Null when
     * the claimant attached nothing.
     */
    val documentRef: String? = null,
    val status: ClaimReviewStatus = ClaimReviewStatus.Pending,
    val createdAtMillis: Long? = null,
    val reviewedAtMillis: Long? = null,
    val reviewedBy: String? = null,
)
